import { createContext, useContext, useEffect, useState } from "react";
import { BrowserRouter, Link, NavLink, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { AlertTriangle, Building2, CalendarDays, CircleHelp, Clock, Link2, LogOut, Menu, Plug, Settings as SettingsIcon, ShieldCheck } from "lucide-react";
import { api, publicApi } from "./lib/api";
import { completeSignIn, config, idToken, loadConfig, relaySignIn, signIn, signOut } from "./lib/auth";
import { mode, originOf, type Mode } from "./lib/host";
import type { Me } from "./lib/types";
import { Button, cx, ErrorNote, Spinner, ToastHost } from "./components/ui";
import { inputCls } from "./components/bits";
import { CompanyMark, Lockup, MicrosoftMark, Wordmark } from "./components/icons";
import Help from "./pages/Help";
import CompanyAdmin from "./pages/CompanyAdmin";
import Platform from "./pages/Platform";
import { FindWorkspace, Join, Signup } from "./pages/Signup";
import SsoTest from "./pages/SsoTest";
import Onboarding from "./pages/Onboarding";
import "./sched/sched.css";
import { Links } from "./sched/pages/Links";
import { LinkEditor } from "./sched/pages/LinkEditor";
import { Bookings } from "./sched/pages/Bookings";
import { BookingDetail } from "./sched/pages/BookingDetail";
import { Connections } from "./sched/pages/Connections";
import { Settings } from "./sched/pages/Settings";
import { PublicBook } from "./sched/pages/PublicBook";
import { Manage } from "./sched/pages/Manage";

// Runs once even under StrictMode's double effect: the sign-in code can only be exchanged once.
let boot: Promise<{ err: string | null; signedIn: boolean }> | null = null;
const MeContext = createContext<{ me: Me; reload: () => Promise<void> } | null>(null);
export const useMe = () => useContext(MeContext)!.me;
export const useReloadMe = () => useContext(MeContext)!.reload;

/** Loads config.json, then picks the app for this host (design §15). */
export default function App() {
  const [m, setM] = useState<Mode | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    loadConfig().then(() => setM(mode()), (e) => setErr((e as Error).message));
  }, []);
  if (err) return <ErrorNote>{err}</ErrorNote>;
  if (!m) return <Spinner />;
  const path = location.pathname.replace(/\/$/, "") || "/";
  if (path === "/help") return <Help />;
  switch (m.kind) {
    case "admin":
      return <AdminApp />;
    case "app":
      if (relaySignIn()) return <Spinner label="Signing in…" />;
      if (path === "/signup") return <Signup />;
      return <AppHost />;
    case "tenant": {
      // People booking a time have no account: their pages never touch Cognito.
      if (/^\/(b|manage)\//.test(location.pathname)) return <PublicApp />;
      if (path === "/join") return <Join />;
      if (path === "/sso-test") return <SsoTest />;
      return <TenantApp slug={m.slug} />;
    }
    default:
      // Any other host (the bare base, an old address): the shared entry point.
      location.replace(`${originOf("app")}${location.pathname === "/" ? "" : location.pathname}${location.search}`);
      return <Spinner />;
  }
}

/** Finishes a sign-in return, then loads /api/me. */
function useSession() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "signin" | "ready">("loading");
  // admin.{base} only reaches /api/platform/* (its tokens are the platform pool's).
  const loadMe = async () => setMe(await api<Me>(mode().kind === "admin" ? "/platform/me" : "/me"));
  useEffect(() => {
    boot ??= (async () => {
      const err = await completeSignIn();
      return { err, signedIn: Boolean(await idToken()) };
    })();
    boot
      .then(async ({ err, signedIn }) => {
        if (err) setError(err);
        if (!signedIn) return setState("signin");
        try {
          await loadMe();
          setState("ready");
        } catch (e) {
          setError((e as Error).message);
          setState("signin");
        }
      })
      .catch((e) => {
        setError((e as Error).message);
        setState("signin");
      });
  }, []);
  return { me, error, state, loadMe };
}

// ---------- app.{base}: sign-up, workspace finder, finishing a new company ----------

function AppHost() {
  const { me, error, state, loadMe } = useSession();
  if (state === "loading") return <Spinner />;
  if (state === "ready" && me) {
    // Already has a workspace: go there.
    if (me.company) {
      location.replace(`${originOf(me.company.slug)}/?${new URLSearchParams({ email: me.email })}`);
      return <Spinner label={`Opening ${me.company.name}…`} />;
    }
    return <Onboarding me={me} onDone={loadMe} />;
  }
  return <FindWorkspace error={error} />;
}

// ---------- admin.{base}: the platform console ----------

function AdminApp() {
  const { me, error, state, loadMe } = useSession();
  if (state === "loading") return <Spinner />;
  if (state !== "ready" || !me) {
    return (
      <AuthFrame>
        <h1 className="text-2xl font-bold font-display">eBookMe platform</h1>
        <p className="text-sm text-muted mt-1">For eBookMe staff</p>
        {error && (
          <div className="mt-5 text-left">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}
        <Button variant="primary" className="w-full h-11 mt-6" onClick={() => signIn({ back: "/" })}>
          Sign in
        </Button>
        <p className="text-xs text-muted mt-6">
          Looking for your company's eBookMe? <a href={originOf("app")}>Find your workspace</a>.
        </p>
      </AuthFrame>
    );
  }
  return (
    <MeContext.Provider value={{ me, reload: loadMe }}>
      <BrowserRouter>
        <Shell>
          <Routes>
            <Route path="*" element={<Platform />} />
          </Routes>
        </Shell>
        <ToastHost />
      </BrowserRouter>
    </MeContext.Provider>
  );
}

// ---------- {slug}.{base}: a tenant's app ----------

function TenantApp({ slug }: { slug: string }) {
  const { me, error, state, loadMe } = useSession();
  if (state === "loading") return <Spinner />;
  if (state === "signin" || !me || !me.company) return <TenantSignIn slug={slug} error={error} />;
  return (
    <MeContext.Provider value={{ me, reload: loadMe }}>
      <BrowserRouter>
        <Shell>
          <Routes>
            <Route element={<SchedOutlet />}>
              <Route path="/links" element={<Links />} />
              <Route path="/links/new" element={<LinkEditor />} />
              <Route path="/links/:slug" element={<LinkEditor />} />
              <Route path="/bookings" element={<Bookings />} />
              <Route path="/bookings/:id" element={<BookingDetail />} />
              <Route path="/connections" element={<Connections />} />
              <Route path="/settings" element={<Settings />} />
            </Route>
            {me.isAdmin && <Route path="/company/*" element={<CompanyAdmin />} />}
            <Route path="*" element={<Navigate to="/links" replace />} />
          </Routes>
        </Shell>
        <ToastHost />
      </BrowserRouter>
    </MeContext.Provider>
  );
}

interface TenantInfo {
  slug: string;
  name: string;
  color: string;
  logo?: string;
  sso: { provider: string } | null;
  enforce: boolean;
}

/** The scheduling pages (BookMe's): scoped styles, and the profile as outlet context. */
function SchedOutlet() {
  const me = useMe();
  return (
    <div className="bk">
      <Outlet context={me.profile ?? null} />
    </div>
  );
}

/** /b/{slug} and /manage/{id} on a company's host: no account. */
function PublicApp() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/b/:slug" element={<PublicBook />} />
        <Route path="/manage/:id" element={<Manage />} />
      </Routes>
    </BrowserRouter>
  );
}

/** The tenant's branded sign-in: its single sign-on when it has one, else email first (design §4, §15). */
function TenantSignIn({ slug, error }: { slug: string; error: string | null }) {
  const back = new URLSearchParams(location.search).get("back") || "/";
  const [info, setInfo] = useState<TenantInfo | null>(null);
  const [missing, setMissing] = useState(false);
  const [email, setEmail] = useState(() => new URLSearchParams(location.search).get("email") ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(error);
  useEffect(() => {
    fetch("/api/public/tenant")
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(setInfo, () => setMissing(true));
  }, []);
  if (missing)
    return (
      <AuthFrame>
        <h1 className="text-xl font-bold font-display">No workspace here</h1>
        <p className="text-sm text-muted mt-2">There's no eBookMe workspace at {slug}.{config().base}.</p>
        <a className="inline-block mt-5 text-accent-text font-medium" href={originOf("app")}>
          Find your workspace
        </a>
      </AuthFrame>
    );
  if (!info) return <Spinner />;
  const go = async () => {
    setBusy(true);
    setErr(null);
    try {
      const d = await publicApi<{ method: string; provider?: string }>("/discover", { email });
      await signIn(d.method === "sso" && d.provider ? { provider: d.provider, loginHint: email, back } : { loginHint: email, back });
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <AuthFrame brand={info}>
      <h1 className="text-2xl font-bold font-display">Sign in</h1>
      <p className="text-sm text-muted mt-1">{info.name} on eBookMe</p>
      {err && (
        <div className="mt-5 text-left">
          <ErrorNote>{err}</ErrorNote>
        </div>
      )}
      {info.sso && (
        <Button variant="primary" className="w-full h-11 mt-6" onClick={() => signIn({ provider: info.sso!.provider, back })}>
          <MicrosoftMark className="size-4" /> Sign in with {info.name}
        </Button>
      )}
      {!info.enforce && (
        <form
          className="mt-5 text-left space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void go();
          }}
        >
          {info.sso && <div className="text-xs text-muted text-center">or with your email</div>}
          <label className="block text-sm font-medium text-ink-2" htmlFor="email">
            Work email
          </label>
          <input id="email" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className={cx(inputCls, "h-11")} placeholder="you@company.com" />
          <Button variant={info.sso ? "secondary" : "primary"} type="submit" className="w-full h-11" busy={busy}>
            Continue
          </Button>
        </form>
      )}
      <p className="text-xs text-muted mt-6">
        Not part of {info.name}? <a href={originOf("app")}>Find your workspace</a>.
      </p>
      <p className="text-xs text-muted mt-3">Booking a meeting with someone? Use their booking link; you don't need an account.</p>
    </AuthFrame>
  );
}

/** The centred card used by sign-in, sign-up and onboarding; a tenant's logo when given. */
export function AuthFrame({ children, wide, brand }: { children: React.ReactNode; wide?: boolean; brand?: { name: string; color: string; logo?: string } }) {
  const landing = config()?.landingUrl ?? "https://ebookme.app";
  return (
    <main className="min-h-full flex flex-col items-center justify-center p-4 gap-6">
      {brand ? (
        <CompanyMark name={brand.name} logo={brand.logo} height={36} />
      ) : (
        <a href={landing} aria-label="eBookMe home">
          <Wordmark height={26} />
        </a>
      )}
      <div className={cx("w-full bg-surface border border-line rounded-2xl shadow-sm text-center overflow-hidden", wide ? "max-w-lg" : "max-w-sm")}>
        <div className={cx("h-1.5", !brand && "brand-bar")} style={brand && /^#[0-9a-f]{6}$/i.test(brand.color) ? { background: brand.color } : undefined} aria-hidden />
        <div className="p-8">{children}</div>
      </div>
      <p className="text-xs text-muted">
        {brand && <>Scheduling by eBookMe · </>}
        <a href="/help" className="hover:text-ink underline-offset-2 hover:underline">
          Help
        </a>{" "}
        ·{" "}
        <a href={`${landing}/terms`} className="hover:text-ink underline-offset-2 hover:underline">
          Terms
        </a>{" "}
        ·{" "}
        <a href={`${landing}/privacy`} className="hover:text-ink underline-offset-2 hover:underline">
          Privacy
        </a>{" "}
        · © {new Date().getFullYear()} eBookMe
      </p>
    </main>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const link = ({ isActive }: { isActive: boolean }) => cx("flex items-center gap-3 rounded-lg px-3 h-10 text-sm font-medium", isActive ? "bg-accent-chip text-accent-text" : "text-ink-2 hover:bg-surface-2");
  const nav = (
    <nav className="flex flex-col gap-1" onClick={() => setOpen(false)}>
      {me.company && (
        <>
          <NavLink to="/links" className={link}>
            <Link2 className="size-4" /> Booking links
          </NavLink>
          <NavLink to="/bookings" className={link}>
            <CalendarDays className="size-4" /> Meetings
          </NavLink>
          <NavLink to="/connections" className={link}>
            <Plug className="size-4" /> Calendars
          </NavLink>
          <NavLink to="/settings" className={link}>
            <SettingsIcon className="size-4" /> Settings
          </NavLink>
          {me.isAdmin && (
            <NavLink to="/company" className={link}>
              <Building2 className="size-4" /> Company
            </NavLink>
          )}
        </>
      )}
      {me.isPlatformAdmin && (
        <NavLink to="/" className={link}>
          <ShieldCheck className="size-4" /> Companies
        </NavLink>
      )}
      <a href="/help" className={link({ isActive: false })}>
        <CircleHelp className="size-4" /> Help
      </a>
    </nav>
  );
  const account = (
    <div className="px-3 flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{me.name}</div>
        <div className="text-xs text-muted truncate">{me.company ? me.company.name : me.isPlatformAdmin ? "eBookMe platform" : me.email}</div>
      </div>
      <button onClick={() => signOut()} className="p-2 rounded-lg text-muted hover:bg-surface-2" aria-label="Sign out" title="Sign out">
        <LogOut className="size-4" />
      </button>
    </div>
  );
  const status = me.company?.status;
  return (
    <div className="flex h-full">
      <aside className="hidden md:flex w-60 shrink-0 flex-col gap-6 border-r border-line bg-surface p-4">
        <Brand />
        {nav}
        <div className="mt-auto">{account}</div>
      </aside>
      {open && (
        <div className="md:hidden fixed inset-0 z-40 bg-scrim" onClick={() => setOpen(false)}>
          <aside className="w-72 h-full bg-surface p-4 flex flex-col gap-6" onClick={(e) => e.stopPropagation()}>
            <Brand />
            {nav}
            <div className="mt-auto">{account}</div>
          </aside>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="md:hidden flex items-center gap-2 border-b border-line bg-surface px-3 h-14">
          <button onClick={() => setOpen(true)} className="p-2 rounded-lg hover:bg-surface-2" aria-label="Menu">
            <Menu className="size-5" />
          </button>
          <Brand />
        </header>
        {me.company?.onTrial && <TrialBanner />}
        {status === "suspended" && (
          <div className="flex items-start gap-2 bg-bad-soft text-bad text-sm px-4 py-2.5 border-b border-line">
            <AlertTriangle className="size-4 mt-0.5 shrink-0" aria-hidden /> <span>{me.company!.name} is paused: booking pages are closed. Contact eBookMe support.</span>
          </div>
        )}
        <main className="flex-1 min-h-0 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}

function TrialBanner() {
  const me = useMe();
  const c = me.company!;
  const days = c.trialEndsAt ? Math.max(0, Math.ceil((Date.parse(c.trialEndsAt) - Date.now()) / 86_400_000)) : 0;
  return (
    <div className="flex items-start gap-2 bg-accent-soft text-ink-2 text-sm px-4 py-2.5 border-b border-line">
      <Clock className="size-4 mt-0.5 shrink-0 text-accent-text" aria-hidden />
      <span>
        <strong>
          {c.plan.name} trial: {days} day{days === 1 ? "" : "s"} left.
        </strong>{" "}
        {me.isAdmin ? (
          <>
            <Link to="/company/billing" className="text-accent-text underline">
              Choose a plan
            </Link>{" "}
            to keep everything, or stay on Free (1 person, 1 link, 1 calendar).
          </>
        ) : (
          <>Ask your company admin to choose a plan.</>
        )}
      </span>
    </div>
  );
}

export function Brand() {
  return (
    <div className="flex items-center px-1">
      <Lockup height={17} />
    </div>
  );
}
